import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Mail, Settings, LogOut, ChevronRight, Search, Trash2, Eye, Calendar, AlertTriangle, X, Sparkles, RefreshCw } from 'lucide-react';
import Header from '../components/Header';
import MainFooter from '../components/MainFooter';
import { aiGenerationsService } from '../services/aiGenerationsService';

// Confirmation Dialog Component - Mobile optimized
const ConfirmationDialog = ({ 
  isOpen, 
  onClose, 
  onConfirm, 
  title, 
  message, 
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  variant = 'danger'
}) => {
  if (!isOpen) return null;

  const variantStyles = {
    danger: {
      icon: 'bg-red-100 text-red-600',
      button: 'bg-red-600 hover:bg-red-700 focus:ring-red-500',
    },
    warning: {
      icon: 'bg-amber-100 text-amber-600',
      button: 'bg-amber-600 hover:bg-amber-700 focus:ring-amber-500',
    }
  };

  const styles = variantStyles[variant];

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />
      
      <div className="relative bg-white w-full sm:max-w-md sm:rounded-2xl rounded-t-3xl shadow-2xl p-5 sm:p-6 animate-in slide-in-from-bottom sm:slide-in-from-bottom-0 sm:zoom-in-95 duration-200">
        {/* Drag indicator for mobile */}
        <div className="sm:hidden w-12 h-1.5 bg-slate-300 rounded-full mx-auto mb-4" />
        
        <button
          onClick={onClose}
          type="button"
          className="absolute top-4 right-4 p-1 text-slate-400 hover:text-slate-600 transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        <div className={`w-12 h-12 rounded-full ${styles.icon} flex items-center justify-center mx-auto mb-4`}>
          <AlertTriangle className="w-6 h-6" />
        </div>

        <h3 className="text-lg font-bold text-slate-800 text-center mb-2">
          {title}
        </h3>
        <p className="text-slate-600 text-center mb-6 text-sm sm:text-base">
          {message}
        </p>

        <div className="flex gap-3">
          <button
            onClick={onClose}
            type="button"
            className="flex-1 px-4 py-3 border-2 border-slate-200 text-slate-700 rounded-xl hover:bg-slate-50 transition-colors font-semibold text-sm sm:text-base active:scale-95"
          >
            {cancelText}
          </button>
          <button
            onClick={() => {
              onConfirm();
              onClose();
            }}
            type="button"
            className={`flex-1 px-4 py-3 text-white rounded-xl transition-colors font-semibold text-sm sm:text-base active:scale-95 ${styles.button}`}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

// Toast Notification Component
const Toast = ({ isOpen, message, type = 'success', onClose }) => {
  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(onClose, 3000);
      return () => clearTimeout(timer);
    }
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const typeStyles = {
    success: 'bg-emerald-600',
    error: 'bg-red-600',
    info: 'bg-blue-600',
  };

  return (
    <div className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-6 sm:bottom-6 z-50 animate-in slide-in-from-bottom-4 fade-in duration-300">
      <div className={`${typeStyles[type]} text-white px-4 sm:px-6 py-3 sm:py-4 rounded-xl shadow-lg flex items-center justify-between gap-3`}>
        <span className="font-medium text-sm sm:text-base">{message}</span>
        <button onClick={onClose} type="button" className="p-1 hover:bg-white/20 rounded-lg transition-colors flex-shrink-0">
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

const ProfileScreen = ({ 
  user,
  userProfile,
  onLogout, 
  currentScreen, 
  onNavigate,
  wishlistCount,
  onProfileUpdate
}) => {
  const [activeTab, setActiveTab] = useState('account');
  const [generationHistory, setGenerationHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [historyError, setHistoryError] = useState(null);
  
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: '',
    message: '',
    confirmText: '',
    variant: 'danger',
    onConfirm: () => {},
  });

  const [toast, setToast] = useState({
    isOpen: false,
    message: '',
    type: 'success',
  });

  const showToast = (message, type = 'success') => {
    setToast({ isOpen: true, message, type });
  };

  const hideToast = useCallback(() => {
    setToast(prev => ({ ...prev, isOpen: false }));
  }, []);

  const showConfirmDialog = ({ title, message, confirmText, variant = 'danger', onConfirm }) => {
    setConfirmDialog({
      isOpen: true,
      title,
      message,
      confirmText,
      variant,
      onConfirm,
    });
  };

  const hideConfirmDialog = () => {
    setConfirmDialog(prev => ({ ...prev, isOpen: false }));
  };

  const menuItems = [
    { 
      id: 'account', 
      label: 'Account Settings', 
      icon: <Settings className="w-5 h-5" />,
    },
    { 
      id: 'searches', 
      label: 'AI Gift Searches', 
      icon: <Search className="w-5 h-5" />,
    },
  ];

  // Load history function with better error handling
  const loadHistory = useCallback(async () => {
    if (!user?.id) {
      console.log('No user ID available for loading history');
      setLoadingHistory(false);
      return;
    }
    
    setLoadingHistory(true);
    setHistoryError(null);
    
    try {
      console.log('Loading generation history for user:', user.id);
      const data = await aiGenerationsService.getGenerations(user.id, 50);
      
      if (Array.isArray(data)) {
        console.log('Loaded', data.length, 'generations');
        setGenerationHistory(data);
      } else {
        console.error('Invalid data format received:', data);
        setGenerationHistory([]);
        setHistoryError('Invalid data format received');
      }
    } catch (error) {
      console.error('Error loading history:', error);
      setHistoryError(error.message || 'Failed to load search history');
      setGenerationHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  }, [user?.id]);

  // Load history on mount and when user changes
  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  // Refresh history when switching to searches tab
  useEffect(() => {
    if (activeTab === 'searches' && user?.id) {
      loadHistory();
    }
  }, [activeTab, user?.id, loadHistory]);

  const handleDeleteGeneration = (generationId) => {
    showConfirmDialog({
      title: 'Delete Search?',
      message: 'This search will be permanently removed.',
      confirmText: 'Delete',
      variant: 'danger',
      onConfirm: async () => {
        try {
          const success = await aiGenerationsService.deleteGeneration(user.id, generationId);
          if (success) {
            setGenerationHistory(prev => prev.filter(g => g.id !== generationId));
            showToast('Search deleted');
          } else {
            showToast('Failed to delete', 'error');
          }
        } catch (error) {
          console.error('Delete error:', error);
          showToast('Failed to delete', 'error');
        }
      },
    });
  };

  const handleClearAll = () => {
    showConfirmDialog({
      title: 'Clear All History?',
      message: `Delete ${generationHistory.length} search${generationHistory.length !== 1 ? 'es' : ''}?`,
      confirmText: 'Clear All',
      variant: 'danger',
      onConfirm: async () => {
        try {
          const success = await aiGenerationsService.clearAllGenerations(user.id);
          if (success) {
            setGenerationHistory([]);
            showToast('History cleared');
          } else {
            showToast('Failed to clear', 'error');
          }
        } catch (error) {
          console.error('Clear all error:', error);
          showToast('Failed to clear', 'error');
        }
      },
    });
  };

  const handleLogout = () => {
    showConfirmDialog({
      title: 'Log Out?',
      message: 'Are you sure you want to log out?',
      confirmText: 'Log Out',
      variant: 'warning',
      onConfirm: onLogout,
    });
  };

  const handleViewGeneration = (generation) => {
    console.log('Viewing generation:', generation);
    onNavigate('aiFinder', { generation });
  };

  const canvasRef = useRef(null);

  // Snowflakes animation
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    
    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    const snowflakes = [];
    const snowColor = '#06402B';
    const numFlakes = 60;

    const drawSnowflake = (x, y, size, rotation, opacity, thickness) => {
      ctx.save();
      ctx.translate(x, y);
      ctx.rotate(rotation);
      ctx.strokeStyle = snowColor;
      ctx.globalAlpha = opacity;
      ctx.lineWidth = thickness;
      ctx.lineCap = 'round';

      for (let i = 0; i < 6; i++) {
        ctx.beginPath();
        ctx.moveTo(0, 0);
        ctx.lineTo(size, 0);
        ctx.moveTo(size * 0.5, 0);
        ctx.lineTo(size * 0.65, -size * 0.2);
        ctx.moveTo(size * 0.5, 0);
        ctx.lineTo(size * 0.65, size * 0.2);
        ctx.moveTo(size * 0.75, 0);
        ctx.lineTo(size * 0.85, -size * 0.15);
        ctx.moveTo(size * 0.75, 0);
        ctx.lineTo(size * 0.85, size * 0.15);
        ctx.stroke();
        ctx.rotate(Math.PI / 3);
      }

      ctx.restore();
    };

    for (let i = 0; i < numFlakes; i++) {
      snowflakes.push({
        x: Math.random() * canvas.width * 1.5 - canvas.width * 0.25,
        y: Math.random() * canvas.height,
        size: Math.random() * 12 + 6,
        speed: Math.random() * 2 + 1,
        opacity: Math.random() * 0.4 + 0.15,
        thickness: Math.random() * 1 + 0.5,
        rotation: Math.random() * Math.PI * 2,
        rotationSpeed: (Math.random() - 0.5) * 0.02,
        drift: Math.random() * 0.3 + 0.1
      });
    }

    let animationId;
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      snowflakes.forEach(flake => {
        drawSnowflake(flake.x, flake.y, flake.size, flake.rotation, flake.opacity, flake.thickness);
        flake.x += flake.speed;
        flake.y += flake.drift;
        flake.rotation += flake.rotationSpeed;

        if (flake.x > canvas.width + flake.size * 2) {
          flake.x = -flake.size * 2;
          flake.y = Math.random() * canvas.height;
        }
        if (flake.y > canvas.height + flake.size * 2) {
          flake.y = -flake.size * 2;
        }
      });

      ctx.globalAlpha = 1;
      animationId = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener('resize', resize);
      cancelAnimationFrame(animationId);
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#FBFCF6] relative">
      {/* <canvas
        ref={canvasRef}
        className="fixed inset-0 pointer-events-none z-0"
      /> */}
      
      <Header 
        user={user} 
        onLogout={onLogout} 
        currentScreen={currentScreen}
        onNavigate={onNavigate}
      />

      <div className="max-w-6xl mx-auto px-3 sm:px-4 md:px-6 py-4 sm:py-6 md:py-8 lg:py-12 relative z-10">
        {/* Mobile: Tab switcher at top */}
        <div className="md:hidden mb-4">
          <div className="flex gap-2 p-1 bg-slate-100 rounded-xl">
            {menuItems.map((item) => (
              <button
                key={item.id}
                onClick={() => setActiveTab(item.id)}
                type="button"
                className={`flex-1 flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                  activeTab === item.id 
                    ? 'bg-white text-[#06402B] shadow-sm' 
                    : 'text-slate-600'
                }`}
              >
                {item.icon}
                <span>{item.id === 'account' ? 'Account' : 'Searches'}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-4 sm:gap-6 md:gap-8">
          {/* Sidebar - Hidden on mobile */}
          <div className="hidden md:block md:col-span-1">
            {/* Profile Card */}
            <div className="bg-white rounded-[5px] shadow-sm p-4 sm:p-6 mb-4 sm:mb-6">
              <div className="flex flex-col items-center text-center">
                <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-[#06402B] text-white flex items-center justify-center text-xl sm:text-2xl font-bold mb-3 sm:mb-4">
                  {user?.email?.[0]?.toUpperCase() || 'U'}
                </div>
                <h2 className="text-lg sm:text-xl font-bold text-slate-800">
                  {userProfile?.full_name || user?.user_metadata?.full_name || 'User'}
                </h2>
                <p className="text-xs sm:text-sm text-slate-600 mt-1 break-all">{user?.email}</p>
                
                {/* Quick Stats */}
                <div className="flex gap-6 mt-4 sm:mt-6 pt-4 sm:pt-6 border-t border-slate-200 w-full justify-center">
                  <button 
                    onClick={() => onNavigate('wishlist')}
                    type="button"
                    className="text-center hover:opacity-80 transition-opacity"
                  >
                    <div className="text-xl sm:text-2xl font-bold text-[#06402B]">{wishlistCount || 0}</div>
                    <div className="text-[10px] sm:text-xs text-slate-600">Wishlist</div>
                  </button>
                  <button 
                    onClick={() => setActiveTab('searches')}
                    type="button"
                    className="text-center hover:opacity-80 transition-opacity"
                  >
                    <div className="text-xl sm:text-2xl font-bold text-[#06402B]">{generationHistory.length}</div>
                    <div className="text-[10px] sm:text-xs text-slate-600">Searches</div>
                  </button>
                </div>
              </div>
            </div>

            {/* Navigation Menu */}
            <div className="bg-white rounded-[5px] shadow-sm overflow-hidden">
              {menuItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => setActiveTab(item.id)}
                  type="button"
                  className={`w-full flex items-center gap-3 px-4 py-3 sm:py-4 text-left transition-colors border-b border-slate-100 last:border-0 text-sm sm:text-base ${
                    activeTab === item.id 
                      ? 'bg-[#FBFCF6] text-[#06402B]' 
                      : 'hover:bg-slate-50 text-slate-700'
                  }`}
                >
                  {item.icon}
                  <span className="font-medium">{item.label}</span>
                  <ChevronRight className="w-4 h-4 ml-auto opacity-50" />
                </button>
              ))}
              
              <button
                onClick={handleLogout}
                type="button"
                className="w-full flex items-center gap-3 px-4 py-3 sm:py-4 text-left text-red-600 hover:bg-red-50 transition-colors text-sm sm:text-base"
              >
                <LogOut className="w-5 h-5" />
                <span className="font-medium">Logout</span>
              </button>
            </div>
          </div>

          {/* Main Content */}
          <div className="md:col-span-2">
            {/* Mobile Profile Card */}
            <div className="md:hidden bg-white rounded-xl shadow-sm p-4 mb-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-[#06402B] text-white flex items-center justify-center text-lg font-bold">
                  {user?.email?.[0]?.toUpperCase() || 'U'}
                </div>
                <div className="flex-1 min-w-0">
                  <h2 className="font-bold text-slate-800 truncate">
                    {userProfile?.full_name || user?.user_metadata?.full_name || 'User'}
                  </h2>
                  <p className="text-xs text-slate-600 truncate">{user?.email}</p>
                </div>
                <button
                  onClick={handleLogout}
                  type="button"
                  className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                >
                  <LogOut className="w-5 h-5" />
                </button>
              </div>
              
              {/* Quick Stats */}
              <div className="flex gap-4 mt-4 pt-4 border-t border-slate-100">
                <button 
                  onClick={() => onNavigate('wishlist')}
                  type="button"
                  className="flex-1 text-center p-2 rounded-lg hover:bg-slate-50 transition-colors"
                >
                  <div className="text-lg font-bold text-[#06402B]">{wishlistCount || 0}</div>
                  <div className="text-[10px] text-slate-600">Wishlist</div>
                </button>
                <button 
                  onClick={() => setActiveTab('searches')}
                  type="button"
                  className="flex-1 text-center p-2 rounded-lg hover:bg-slate-50 transition-colors"
                >
                  <div className="text-lg font-bold text-[#06402B]">{generationHistory.length}</div>
                  <div className="text-[10px] text-slate-600">Searches</div>
                </button>
              </div>
            </div>

            <div className="bg-white rounded-xl sm:rounded-[5px] shadow-sm p-4 sm:p-6">
              {activeTab === 'account' && (
                <div>
                  <h3 className="text-base sm:text-lg font-bold text-slate-800 mb-4 sm:mb-6">Account Settings</h3>
                  
                  <form onSubmit={async (e) => {
                    e.preventDefault();
                    const formData = new FormData(e.target);
                    const updates = {
                      full_name: formData.get('full_name'),
                      phone_number: formData.get('phone_number'),
                    };
                    
                    try {
                      const success = await onProfileUpdate(updates);
                      showToast(success ? 'Profile updated!' : 'Update failed', success ? 'success' : 'error');
                    } catch (error) {
                      showToast('Update failed', 'error');
                    }
                  }}>
                    <div className="space-y-4 sm:space-y-6">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1.5 sm:mb-2">
                          Full Name
                        </label>
                        <input
                          type="text"
                          name="full_name"
                          defaultValue={userProfile?.full_name || user?.user_metadata?.full_name || ''}
                          className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border-2 border-slate-200 rounded-xl sm:rounded-[5px] focus:border-[#06402B] focus:ring-2 focus:ring-[#06402B]/20 outline-none text-sm sm:text-base"
                          placeholder="Enter your full name"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1.5 sm:mb-2">
                          Email Address
                        </label>
                        <div className="flex items-center gap-2 px-3 sm:px-4 py-2.5 sm:py-3 bg-slate-100 border-2 border-slate-200 rounded-xl sm:rounded-[5px] text-slate-600 text-sm sm:text-base">
                          <Mail className="w-4 h-4 flex-shrink-0" />
                          <span className="truncate">{user?.email}</span>
                        </div>
                        <p className="text-[10px] sm:text-xs text-slate-500 mt-1">
                          Email cannot be changed
                        </p>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1.5 sm:mb-2">
                          Phone Number
                        </label>
                        <input
                          type="tel"
                          name="phone_number"
                          defaultValue={userProfile?.phone_number || ''}
                          className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border-2 border-slate-200 rounded-xl sm:rounded-[5px] focus:border-[#06402B] focus:ring-2 focus:ring-[#06402B]/20 outline-none text-sm sm:text-base"
                          placeholder="Enter your phone number"
                        />
                      </div>
                      
                      <button 
                        type="submit" 
                        className="w-full sm:w-auto px-6 py-2.5 sm:py-3 bg-[#06402B] text-white rounded-xl sm:rounded-[5px] hover:bg-[#053527] transition-colors font-semibold text-sm sm:text-base active:scale-95"
                      >
                        Save Changes
                      </button>
                    </div>
                  </form>
                </div>
              )}

              {activeTab === 'searches' && (
                <div className="relative">
                  <div className="flex items-center justify-between mb-4 sm:mb-6">
                    <div className="flex items-center gap-2">
                      <Sparkles className="w-5 h-5 text-[#06402B]" />
                      <h3 className="text-base sm:text-lg font-bold text-slate-800">AI Gift Searches</h3>
                    </div>
                    <div className="flex items-center gap-2">
                      {/* Refresh button */}
                      <button
                        onClick={loadHistory}
                        disabled={loadingHistory}
                        type="button"
                        className="p-2 text-slate-500 hover:text-[#06402B] hover:bg-slate-100 rounded-lg transition-colors disabled:opacity-50"
                        title="Refresh"
                      >
                        <RefreshCw className={`w-4 h-4 ${loadingHistory ? 'animate-spin' : ''}`} />
                      </button>
                      {generationHistory.length > 0 && (
                        <button
                          onClick={handleClearAll}
                          type="button"
                          className="text-xs sm:text-sm text-red-600 hover:text-red-700 font-medium flex items-center gap-1"
                        >
                          <Trash2 className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                          <span className="hidden sm:inline">Clear All</span>
                        </button>
                      )}
                    </div>
                  </div>


                  

                  {/* Error State */}
                  {historyError && !loadingHistory && (
                    <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-4">
                      <p className="text-sm text-red-700">{historyError}</p>
                      <button
                        onClick={loadHistory}
                        type="button"
                        className="mt-2 text-sm text-red-600 font-medium hover:underline"
                      >
                        Try again
                      </button>
                    </div>
                  )}

                  {loadingHistory ? (
                    <div className="text-center py-8 sm:py-12">
                      <div className="w-10 h-10 sm:w-12 sm:h-12 border-4 border-[#06402B] border-t-transparent rounded-full animate-spin mx-auto mb-3 sm:mb-4"></div>
                      <p className="text-slate-600 text-sm sm:text-base">Loading history...</p>
                    </div>
                  ) : generationHistory.length === 0 && !historyError ? (
                    <div className="text-center py-8 sm:py-12">
                      <Search className="w-12 h-12 sm:w-16 sm:h-16 text-slate-300 mx-auto mb-3 sm:mb-4" />
                      <p className="text-slate-600 mb-2 text-sm sm:text-base">No search history yet</p>
                      <p className="text-xs sm:text-sm text-slate-500 mb-4 sm:mb-6">
                        Your AI gift searches will appear here
                      </p>
                      <button
                        onClick={() => onNavigate('aiFinder')}
                        type="button"
                        className="px-5 py-2.5 sm:px-6 sm:py-3 bg-[#06402B] text-white rounded-xl sm:rounded-[5px] hover:bg-[#053527] transition-colors font-semibold text-sm sm:text-base active:scale-95"
                      >
                        Start Searching
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-2 sm:space-y-3">
                      {generationHistory.map((gen) => (
                        <div
                          key={gen.id}
                          className="p-3 sm:p-4 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors"
                        >
                          <div className="flex items-start justify-between gap-2 mb-2 sm:mb-3">
                            <div className="flex-1 min-w-0">
                              {/* Date */}
                              <div className="flex items-center gap-1.5 sm:gap-2 mb-1.5 sm:mb-2">
                                <Calendar className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-slate-400 flex-shrink-0" />
                                <span className="text-[10px] sm:text-xs text-slate-500">
                                  {new Date(gen.created_at).toLocaleDateString('en-US', {
                                    month: 'short',
                                    day: 'numeric',
                                    year: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  })}
                                </span>
                              </div>
                              
                              {/* Search Query */}
                              <p className="text-xs sm:text-sm text-slate-700 font-medium mb-2 line-clamp-2">
                                {gen.search_query || 'Gift search'}
                              </p>
                              
                              {/* Tags */}
                              <div className="flex flex-wrap gap-1 sm:gap-1.5">
                                {gen.occasions?.slice(0, 2).map((occ, i) => (
                                  <span key={`occ-${i}`} className="text-[10px] sm:text-xs px-1.5 sm:px-2 py-0.5 sm:py-1 bg-[#06402B]/10 text-[#06402B] rounded-full font-medium">
                                    {occ}
                                  </span>
                                ))}
                                {gen.relationships?.slice(0, 1).map((rel, i) => (
                                  <span key={`rel-${i}`} className="text-[10px] sm:text-xs px-1.5 sm:px-2 py-0.5 sm:py-1 bg-blue-100 text-blue-700 rounded-full font-medium">
                                    {rel}
                                  </span>
                                ))}
                                {gen.personality?.slice(0, 1).map((pers, i) => (
                                  <span key={`pers-${i}`} className="text-[10px] sm:text-xs px-1.5 sm:px-2 py-0.5 sm:py-1 bg-orange-100 text-orange-700 rounded-full font-medium">
                                    {pers}
                                  </span>
                                ))}
                              </div>
                              
                              {/* Results count */}
                              <p className="text-[10px] sm:text-xs text-slate-500 mt-1.5 sm:mt-2">
                                {gen.results_count || gen.results?.length || 0} results
                              </p>
                            </div>
                            
                            {/* Action buttons */}
                            <div className="flex gap-1 sm:gap-2 flex-shrink-0">
                              <button
                                onClick={() => handleViewGeneration(gen)}
                                type="button"
                                className="p-1.5 sm:p-2 text-[#06402B] hover:bg-[#06402B]/10 rounded-lg transition-colors"
                                title="View results"
                              >
                                <Eye className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDeleteGeneration(gen.id)}
                                type="button"
                                className="p-1.5 sm:p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                title="Delete"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <MainFooter />

      <ConfirmationDialog
        isOpen={confirmDialog.isOpen}
        onClose={hideConfirmDialog}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmText={confirmDialog.confirmText}
        variant={confirmDialog.variant}
      />

      <Toast
        isOpen={toast.isOpen}
        message={toast.message}
        type={toast.type}
        onClose={hideToast}
      />
    </div>
  );
};

export default ProfileScreen;